// src/contexts/AuthContext.jsx - FIXED VERSION with Proper Auth Management
import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';
import { useSocket } from './SocketContext';
import SessionExpiredModal from '../components/auth/SessionExpiredModal';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [allBusinesses, setAllBusinesses] = useState([]);  // All businesses user has access to
  const [switchingBusiness, setSwitchingBusiness] = useState(false);  // Loading state for business switch
  const [sessionExpired, setSessionExpired] = useState(false);  // Show session expired modal
  
  const socket = useSocket();
  
  // ========================================
  // 🎓 LEARNING: Refs for Managing State
  // ========================================
  // Refs persist across re-renders and don't trigger re-renders when changed
  // Perfect for flags and tracking async operations
  
  const isInitialized = useRef(false);        // Has auth been initialized?
  const interceptorId = useRef(null);         // Track axios interceptor
  const isRefreshing = useRef(false);         // Prevent concurrent refresh attempts
  const refreshPromise = useRef(null);        // Share refresh promise across requests
  const failedQueue = useRef([]);             // Queue failed requests during refresh
  const isMounted = useRef(true);             // Prevent state updates after unmount

  // ========================================
  // 🎓 LEARNING: Proper Queue Management
  // ========================================
  // When refresh fails, we need to reject ALL queued requests
  // When refresh succeeds, we need to retry ALL queued requests
  
  const processQueue = useCallback((error, token = null) => {
    console.log(`🔄 Processing queue: ${failedQueue.current.length} requests, error: ${!!error}`);
    
    failedQueue.current.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error);
      } else {
        resolve(token);
      }
    });
    
    failedQueue.current = [];
  }, []);

  
  const resetAuthState = useCallback((reason = 'manual') => {
    console.log('🧹 Resetting auth state completely, reason:', reason);

    // **CRITICAL FIX**: Disconnect WebSocket FIRST, before clearing tokens
    if (socket) {
      console.log('👋 Disconnecting WebSocket due to auth reset');
      socket.disconnect(); // This properly cleans up the WebSocket
    }

    // Broadcast reset event to all providers
    window.dispatchEvent(new CustomEvent('auth-state-reset'));

    // Clear tokens from storage
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');

    // Clear axios headers
    delete axios.defaults.headers.common['Authorization'];

    // Reset all React state (but only if component is still mounted)
    if (isMounted.current) {
      setCurrentUser(null);
      setIsAuthenticated(false);
      setError('');
      setLoading(false);

      // Show session expired modal when token refresh fails (not manual logout)
      if (reason === 'token_expired') {
        setSessionExpired(true);
      }
    }

    // Reset refs
    isInitialized.current = false;
    isRefreshing.current = false;
    refreshPromise.current = null;

    // Process any queued requests with error
    processQueue(new Error('Authentication failed'));

  }, [socket, processQueue]);

  // ========================================
  // 🎓 LEARNING: Smart Token Refresh
  // ========================================
  // Only ONE refresh attempt at a time, others wait for the result
  
  const refreshAccessToken = useCallback(async () => {
    console.log('🔄 refreshAccessToken called');
    
    // If already refreshing, return the existing promise
    if (isRefreshing.current && refreshPromise.current) {
      console.log('⏳ Refresh already in progress, returning existing promise');
      return refreshPromise.current;
    }
    
    // Mark as refreshing and create promise
    isRefreshing.current = true;
    refreshPromise.current = (async () => {
      try {
        const refreshToken = localStorage.getItem('refresh_token');

        if (!refreshToken) {
          throw new Error('No refresh token available');
        }

        console.log('🔄 Attempting token refresh...');
        // Refresh token must be sent in Authorization header (not body)
        const response = await axios.post(
          `${API_ENDPOINTS.businessAuth}/refresh`,
          {},
          {
            headers: {
              'Authorization': `Bearer ${refreshToken}`
            }
          }
        );

        if (response.data.status === 'success') {
          const { access_token } = response.data.data;
          
          // Store new token
          localStorage.setItem('access_token', access_token);
          axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
          
          console.log('✅ Token refreshed successfully');
          
          // Reconnect WebSocket with new token
          if (socket && socket.isAuthenticated) {
            console.log('🔄 Reconnecting WebSocket with new token...');
            await socket.reconnect(access_token);
          }
          
          // Process queued requests with new token
          processQueue(null, access_token);
          
          return access_token;
        } else {
          throw new Error('Token refresh failed');
        }
      } catch (error) {
        console.error('❌ Token refresh failed:', error);

        // Complete auth reset on refresh failure — show session expired modal
        resetAuthState('token_expired');
        
        // Process queue with error
        processQueue(error);
        
        throw error;
      } finally {
        // Reset refresh state
        isRefreshing.current = false;
        refreshPromise.current = null;
      }
    })();
    
    return refreshPromise.current;
  }, [socket, processQueue, resetAuthState]);

  // ========================================
  // 🎓 LEARNING: Robust Axios Interceptor
  // ========================================
  // Handles token refresh transparently for all API calls
  
  useEffect(() => {
    console.log('🔧 Setting up axios interceptor');
    
    interceptorId.current = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // Only handle 401 errors
        if (error.response?.status !== 401 || originalRequest._retry) {
          return Promise.reject(error);
        }

        console.log('🔍 401 error detected:', originalRequest.url);

        // Don't refresh for auth endpoints (prevents infinite loops)
        if (originalRequest.url?.includes('/login') || 
            originalRequest.url?.includes('/refresh') ||
            originalRequest.url?.includes('/verify-partner-code') ||
            originalRequest.url?.includes('/complete-partner-setup')) {
          console.log('🚫 Auth endpoint failed, not attempting refresh');
          resetAuthState();
          return Promise.reject(error);
        }

        // Mark as retry attempt
        originalRequest._retry = true;

        try {
          // If already refreshing, queue this request
          if (isRefreshing.current) {
            console.log('⏳ Queueing request during refresh');
            return new Promise((resolve, reject) => {
              failedQueue.current.push({ 
                resolve: (token) => {
                  originalRequest.headers['Authorization'] = `Bearer ${token}`;
                  resolve(axios(originalRequest));
                },
                reject
              });
            });
          }

          // Attempt refresh
          const newToken = await refreshAccessToken();
          
          // Retry original request with new token
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
          return axios(originalRequest);
          
        } catch (refreshError) {
          console.error('❌ Request failed after refresh attempt');
          return Promise.reject(refreshError);
        }
      }
    );

    // Cleanup interceptor
    return () => {
      if (interceptorId.current !== null) {
        console.log('🧹 Cleaning up axios interceptor');
        axios.interceptors.response.eject(interceptorId.current);
        interceptorId.current = null;
      }
    };
  }, [refreshAccessToken, resetAuthState]);

  // ========================================
  // 🎓 LEARNING: Safe Auth Check
  // ========================================
  // Only runs once, with proper error handling
  
  const checkAuth = useCallback(async () => {
    if (isInitialized.current) {
      console.log('🔄 Auth already initialized, skipping');
      return;
    }

    try {
      console.log('🔍 Initializing auth check...');
      isInitialized.current = true;
      
      const token = localStorage.getItem('access_token');
      
      if (!token) {
        console.log('❌ No token found');
        if (isMounted.current) {
          setLoading(false);
        }
        return;
      }

      // Set token in headers
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      // Verify token
      console.log('🔍 Verifying token...');
      const response = await axios.get(`${API_ENDPOINTS.businessAuth}/me`);
      
      if (response.data.status === 'success' && isMounted.current) {
        console.log('✅ Token verified, user authenticated');

        // Combine user and business data if available
        const userData = response.data.data;
        const fullUserData = userData.business ? {
          ...userData.user || userData,
          business: userData.business
        } : userData;

        setCurrentUser(fullUserData);
        setIsAuthenticated(true);
        setAllBusinesses(userData.all_businesses || []);  // Store all businesses

        // Connect WebSocket
        if (socket) {
          console.log('🔌 Connecting WebSocket...');
          await socket.connect(token);
        }
      }
    } catch (error) {
      console.error('❌ Auth check failed:', error);
      // Don't call resetAuthState here - let interceptor handle it
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  }, [socket]);

  // ========================================
  // 🎓 LEARNING: Component Lifecycle
  // ========================================
  // Track mounting state to prevent memory leaks
  
  useEffect(() => {
    isMounted.current = true;
    checkAuth();

    return () => {
      isMounted.current = false;
    };
  }, [checkAuth]);

  // ========================================
  // PROFILE UPDATE LISTENER
  // ========================================
  // Listen for profile updates and refresh currentUser

  useEffect(() => {
    if (!socket?.universalHandler) return;

    const handleProfileUpdate = (data) => {
      console.log('👤 AuthContext: Profile updated, refreshing currentUser', data);

      if (isMounted.current && data.data) {
        // Update currentUser with new profile data while preserving business info
        setCurrentUser(prevUser => ({
          ...prevUser,
          ...data.data,
          business: prevUser?.business // Preserve business data
        }));
      }
    };

    socket.universalHandler.on('user_profile_updated', handleProfileUpdate);

    return () => {
      if (socket?.universalHandler) {
        socket.universalHandler.off('user_profile_updated', handleProfileUpdate);
      }
    };
  }, [socket]);

  // ========================================
  // PUBLIC AUTH METHODS
  // ========================================
  
  const login = async (email, password) => {
    try {
      setError('');
      setLoading(true);
      setSessionExpired(false);

      console.log('🔍 Attempting login...');
      const response = await axios.post(`${API_ENDPOINTS.businessAuth}/login`, {
        email,
        password
      });

      if (response.data.status === 'success') {
        const responseData = response.data.data;

        // Check if this requires business selection (multi-business user)
        if (responseData.requires_business_selection) {
          console.log('🏢 Multi-business user detected, auto-selecting business...');

          const { selection_token, businesses } = responseData;

          // Try to find the last used business
          const lastBusinessId = localStorage.getItem('last_business_id');
          let selectedBusinessId = null;

          if (lastBusinessId) {
            // Check if last business is still in the available list
            const lastBusiness = businesses.find(b => b.id === lastBusinessId);
            if (lastBusiness) {
              console.log('✅ Found last used business:', lastBusiness.name);
              selectedBusinessId = lastBusinessId;
            }
          }

          // If no last business or it's not valid, use the first one
          if (!selectedBusinessId && businesses.length > 0) {
            selectedBusinessId = businesses[0].id;
            console.log('📌 Using first available business:', businesses[0].name);
          }

          if (!selectedBusinessId) {
            throw new Error('No businesses available');
          }

          // Call select-business to get actual JWT tokens
          const selectResponse = await axios.post(`${API_ENDPOINTS.businessAuth}/select-business`, {
            selection_token: selection_token,
            business_id: selectedBusinessId
          });

          if (selectResponse.data.status !== 'success') {
            throw new Error(selectResponse.data.message || 'Business selection failed');
          }

          // Use the select-business response data
          const { access_token, refresh_token, user, business, all_businesses } = selectResponse.data.data;

          console.log('✅ Business selected:', business.name);

          // Store tokens and last business
          localStorage.setItem('access_token', access_token);
          localStorage.setItem('refresh_token', refresh_token);
          localStorage.setItem('last_business_id', business.id);

          axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

          const fullUserData = {
            ...user,
            business: business
          };

          if (isMounted.current) {
            setCurrentUser(fullUserData);
            setIsAuthenticated(true);
            setAllBusinesses(all_businesses || []);
          }

          if (socket) {
            console.log('🔌 Connecting WebSocket after login...');
            await socket.connect(access_token);
          }

          return true;
        }

        // Single business - direct login (original flow)
        const { access_token, refresh_token, user, business, all_businesses } = responseData;

        console.log('✅ Login successful (single business)');

        // Store tokens and last business
        localStorage.setItem('access_token', access_token);
        localStorage.setItem('refresh_token', refresh_token);
        localStorage.setItem('last_business_id', business.id);

        // Set authorization header
        axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

        // Combine user and business data
        const fullUserData = {
          ...user,
          business: business  // This adds the business object with the slug
        };

        // Update state (only if mounted)
        if (isMounted.current) {
          setCurrentUser(fullUserData);
          setIsAuthenticated(true);
          setAllBusinesses(all_businesses || []);  // Store all businesses for switcher
        }

        // Connect WebSocket
        if (socket) {
          console.log('🔌 Connecting WebSocket after login...');
          await socket.connect(access_token);
        }

        return true;
      } else {
        if (isMounted.current) {
          setError(response.data.message || 'Login failed');
        }
        return false;
      }
    } catch (error) {
      console.error('❌ Login error:', error);
      if (isMounted.current) {
        setError(error.response?.data?.message || 'Login failed. Please try again.');
      }
      return false;
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  const logout = async () => {
    console.log('🔍 Logging out...');
    resetAuthState();
  };

  // ========================================
  // BUSINESS SWITCHING
  // ========================================

  const switchBusiness = async (businessId) => {
    try {
      if (!businessId) {
        setError('Business ID is required');
        return false;
      }

      // Don't switch if already on this business
      if (currentUser?.business?.id === businessId) {
        console.log('🔄 Already on this business, skipping switch');
        return true;
      }

      console.log('🔄 Switching to business:', businessId);
      setSwitchingBusiness(true);
      setError('');

      const response = await axios.post(`${API_ENDPOINTS.businessAuth}/switch-business`, {
        business_id: businessId
      });

      if (response.data.status === 'success') {
        const { access_token, refresh_token, user, business, all_businesses } = response.data.data;

        console.log('✅ Business switch successful:', business.name);

        // Store new tokens and last business
        localStorage.setItem('access_token', access_token);
        localStorage.setItem('refresh_token', refresh_token);
        localStorage.setItem('last_business_id', business.id);
        axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

        // Update user data with new business context
        const fullUserData = {
          ...user,
          business: business
        };

        // Disconnect socket (this will trigger auth-state-reset via resetAuthState pattern)
        if (socket) {
          console.log('🔌 Disconnecting WebSocket for business switch...');
          socket.disconnect();
        }

        // Dispatch auth-state-reset to clear all stores
        window.dispatchEvent(new CustomEvent('auth-state-reset'));

        // Update state with new business
        if (isMounted.current) {
          setCurrentUser(fullUserData);
          setAllBusinesses(all_businesses || []);
        }

        // Reconnect socket with new token (this triggers store reinitialization)
        if (socket) {
          console.log('🔌 Reconnecting WebSocket with new business context...');
          await socket.connect(access_token);
        }

        return true;
      } else {
        if (isMounted.current) {
          setError(response.data.message || 'Business switch failed');
        }
        return false;
      }
    } catch (error) {
      console.error('❌ Business switch error:', error);
      if (isMounted.current) {
        setError(error.response?.data?.message || 'Failed to switch business. Please try again.');
      }
      return false;
    } finally {
      if (isMounted.current) {
        setSwitchingBusiness(false);
      }
    }
  };

  // Partner signup methods (simplified - same pattern)
  const verifyPartnerCode = async (code) => {
    try {
      setError('');
      setLoading(true);

      const response = await axios.post(`${API_ENDPOINTS.businessAuth}/verify-partner-code`, {
        partner_code: code
      });

      if (response.data.status === 'success') {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Code verification failed');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Code verification failed';
      if (isMounted.current) {
        setError(errorMessage);
      }
      throw new Error(errorMessage);
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  const completePartnerSetup = async (partnerCode, name, email, password) => {
    try {
      setError('');
      setLoading(true);

      const response = await axios.post(`${API_ENDPOINTS.businessAuth}/complete-partner-setup`, {
        partner_code: partnerCode,
        name,
        email,
        password
      });

      if (response.data.status === 'success') {
        const { access_token, refresh_token, user, business, all_businesses } = response.data.data;

        localStorage.setItem('access_token', access_token);
        localStorage.setItem('refresh_token', refresh_token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

        const fullUserData = {
          ...user,
          business: business
        };

        if (isMounted.current) {
          setCurrentUser(fullUserData);
          setIsAuthenticated(true);
          setAllBusinesses(all_businesses || []);
        }

        if (socket) {
          await socket.connect(access_token);
        }

        return true;
      } else {
        if (isMounted.current) {
          setError(response.data.message || 'Partner setup failed');
        }
        return false;
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Partner setup failed';
      if (isMounted.current) {
        setError(errorMessage);
      }
      return false;
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  // Beta registration methods
  const verifyBetaCode = async (code) => {
    try {
      setError('');
      setLoading(true);

      const response = await axios.post(`${API_ENDPOINTS.auth}/verify-beta-code`, {
        beta_code: code
      });

      if (response.data.status === 'success') {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Beta code verification failed');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Beta code verification failed';
      if (isMounted.current) {
        setError(errorMessage);
      }
      throw new Error(errorMessage);
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  const registerWithBetaCode = async (betaCode, businessName, adminName, adminEmail, adminPassword) => {
    try {
      setError('');
      setLoading(true);

      const response = await axios.post(`${API_ENDPOINTS.auth}/register`, {
        beta_code: betaCode,
        business_name: businessName,
        admin_name: adminName,
        admin_email: adminEmail,
        admin_password: adminPassword
      });

      if (response.data.status === 'success') {
        const { access_token, refresh_token, user, business, all_businesses } = response.data.data;

        localStorage.setItem('access_token', access_token);
        localStorage.setItem('refresh_token', refresh_token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

        const fullUserData = {
          ...user,
          business: business
        };

        if (isMounted.current) {
          setCurrentUser(fullUserData);
          setIsAuthenticated(true);
          setAllBusinesses(all_businesses || []);
        }

        if (socket) {
          await socket.connect(access_token);
        }

        return true;
      } else {
        if (isMounted.current) {
          setError(response.data.message || 'Registration failed');
        }
        return false;
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Registration failed';
      if (isMounted.current) {
        setError(errorMessage);
      }
      return false;
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  const clearError = () => {
    if (isMounted.current) {
      setError('');
    }
  };

  const clearSessionExpired = useCallback(() => {
    if (isMounted.current) {
      setSessionExpired(false);
    }
  }, []);

  const refreshUser = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        console.log('❌ No token available for refresh');
        return false;
      }

      console.log('🔄 Refreshing user data...');
      const response = await axios.get(`${API_ENDPOINTS.businessAuth}/me`);

      if (response.data.status === 'success' && isMounted.current) {
        console.log('✅ User data refreshed');

        // Update user data with latest from server
        const userData = response.data.data;
        const fullUserData = userData.business ? {
          ...userData.user || userData,
          business: userData.business
        } : userData;

        setCurrentUser(fullUserData);
        return true;
      }
      return false;
    } catch (error) {
      console.error('❌ Failed to refresh user data:', error);
      return false;
    }
  };

  const value = {
    currentUser,
    isAuthenticated,
    loading,
    error,
    allBusinesses,
    switchingBusiness,
    sessionExpired,
    login,
    logout,
    switchBusiness,
    verifyPartnerCode,
    completePartnerSetup,
    verifyBetaCode,
    registerWithBetaCode,
    clearError,
    clearSessionExpired,
    refreshUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      <SessionExpiredModal open={sessionExpired} />
    </AuthContext.Provider>
  );
};